import { Action, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Conversation, ConversationResponse, Message } from '../../Types/conversation';
import { PaginatedResult } from '../../Types/pagination';
import {
  getConversation,
  getConversations,
  getFolderAgnosticConversations,
  patchMessage,
} from '../actions/conversationActions';
import { CONVERSATION_LIST_SIZE } from '../consts';
import { RootState } from '../store';

interface ConversationsState {
  conversations: Conversation[];
  currentConversation?: Conversation;
  currentMessages: Message[];
  totalCount: number;
  isLoading: boolean;
  isLoadingConversations: boolean;
  isScrolling: boolean;
  searchQuery: string;
  page: number;
  currentNumTokens: number;
  maxNumTokens: number;
  conversationSearchSelected: boolean;
  folderAgnosticConversations: Conversation[];
  folderAgnosticTotalCount: number;
  folderAgnosticIsLoading: boolean;
  folderAgnosticIsScrolling: boolean;
  folderAgnosticPage: number;
}

const initialState: ConversationsState = {
  conversations: [],
  currentConversation: undefined,
  currentMessages: [],
  totalCount: 0,
  isLoading: false,
  isLoadingConversations: false,
  isScrolling: false,
  searchQuery: '',
  page: 1,
  currentNumTokens: 0,
  maxNumTokens: 128000,
  conversationSearchSelected: false,
  folderAgnosticConversations: [],
  folderAgnosticTotalCount: 0,
  folderAgnosticIsLoading: false,
  folderAgnosticIsScrolling: false,
  folderAgnosticPage: 1,
};

// Define the type for the rejected action
interface RejectedAction extends Action {
  payload: string;
}

const conversationsSlice = createSlice({
  name: 'conversations',
  initialState,
  reducers: {
    setConversations: (state, action: PayloadAction<Conversation[]>) => {
      state.conversations = action.payload;
    },
    setCurrentConversation: (state, action: PayloadAction<Conversation | undefined>) => {
      state.currentConversation = action.payload;
    },
    setCurrentMessages: (state, action: PayloadAction<Message[]>) => {
      state.currentMessages = action.payload;
    },
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsLoadingConversations: (state, action: PayloadAction<boolean>) => {
      state.isLoadingConversations = action.payload;
    },
    setIsScrolling: (state, action: PayloadAction<boolean>) => {
      state.isScrolling = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setCurrentNumTokens: (state, action: PayloadAction<number>) => {
      state.currentNumTokens = action.payload;
    },
    setMaxNumTokens: (state, action: PayloadAction<number>) => {
      state.maxNumTokens = action.payload;
    },
    setConversationSearchSelected: (state, action: PayloadAction<boolean>) => {
      state.conversationSearchSelected = action.payload;
    },
    setFolderAgnosticTotalCount: (state, action: PayloadAction<number>) => {
      state.folderAgnosticTotalCount = action.payload;
    },
    setFolderAgnosticIsLoading: (state, action: PayloadAction<boolean>) => {
      state.folderAgnosticIsLoading = action.payload;
    },
    setFolderAgnosticIsScrolling: (state, action: PayloadAction<boolean>) => {
      state.folderAgnosticIsScrolling = action.payload;
    },
    setFolderAgnosticPage: (state, action: PayloadAction<number>) => {
      state.folderAgnosticPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        getConversations.fulfilled,
        (state, action: PayloadAction<PaginatedResult<Conversation>>) => {
          const { items, total } = action.payload;
          if (state.page === 0) {
            state.conversations = items;
          } else {
            state.conversations = [...state.conversations, ...items];
          }
          state.totalCount = total;
          state.isLoadingConversations = false;
        }
      )
      .addCase(
        getFolderAgnosticConversations.fulfilled,
        (state, action: PayloadAction<ConversationResponse>) => {
          const { data, totalCount } = action.payload;
          const pageOffset = CONVERSATION_LIST_SIZE * (state.folderAgnosticPage - 1);
          if (pageOffset === 0) {
            state.folderAgnosticConversations = data;
          } else {
            state.folderAgnosticConversations = [...state.folderAgnosticConversations, ...data];
          }
          state.folderAgnosticTotalCount = totalCount;
          state.folderAgnosticIsLoading = false;
        }
      )
      .addCase(getConversation.fulfilled, (state, action: PayloadAction<Conversation>) => {
        state.currentConversation = action.payload;
        state.isLoading = false;
      })
      .addCase(patchMessage.fulfilled, (state, action: PayloadAction<Message>) => {
        state.isLoading = false;
        const updatedMessage = action.payload;
        const messageIndex = state.currentMessages.findIndex(
          (message) => message.id === updatedMessage.id
        );
        if (messageIndex !== -1) {
          state.currentMessages[messageIndex] = updatedMessage;
        }
      })
      .addMatcher(
        (action: Action): action is RejectedAction =>
          action.type.startsWith('conversations/') && action.type.endsWith('/rejected'),
        (state) => {
          state.isLoading = false;
          state.isLoadingConversations = false;
        }
      );
  },
});

export const {
  setConversationSearchSelected,
  setCurrentConversation,
  setCurrentMessages,
  setIsLoading,
  setIsLoadingConversations,
  setIsScrolling,
  setPage,
  setSearchQuery,
  setCurrentNumTokens,
  setMaxNumTokens,
  setFolderAgnosticTotalCount,
  setFolderAgnosticIsScrolling,
  setFolderAgnosticIsLoading,
  setFolderAgnosticPage,
} = conversationsSlice.actions;

export const selectConversations = (state: RootState) => state.conversations;

export default conversationsSlice.reducer;
